import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { KeyRound, ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { MolecularBg } from "../components/MolecularBg";
import { useSEO } from "../lib/useSEO";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";

export default function ResetPasswordPage() {
    useSEO({
        title: "Reset Password — Carbon Labs Australia",
        description: "Set a new password for your Carbon Labs Australia account.",
        canonical: "https://www.carbonlabsau.com/reset-password",
    });

    const [params] = useSearchParams();
    const navigate = useNavigate();
    const { setSessionFromReset } = useAuth() || {};
    const token = params.get("token") || "";

    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [tokenValid, setTokenValid] = useState(true);

    useEffect(() => {
        if (!token || token.length < 20) setTokenValid(false);
    }, [token]);

    const submit = async (e) => {
        e.preventDefault();
        if (password.length < 8) return toast.error("Password must be at least 8 characters.");
        if (password !== confirm) return toast.error("Passwords do not match.");
        setSubmitting(true);
        try {
            const { data } = await api.post("/auth/reset-password", { token, new_password: password });
            // Auto-login the user with the new session token
            if (data.token && setSessionFromReset) {
                await setSessionFromReset(data.token, data.user);
            }
            setDone(true);
            setTimeout(() => navigate(data.token ? "/account" : "/login"), 2000);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Couldn't reset password. Request a new link.");
            if (typeof detail === "string" && (/expired|invalid|already/i).test(detail)) {
                setTokenValid(false);
            }
        }
        setSubmitting(false);
    };

    return (
        <div className="pt-28 pb-20 min-h-screen relative" data-testid="reset-password-page">
            <MolecularBg intensity="low" />
            <div className="container-x max-w-md mx-auto relative z-10">
                <Link to="/login" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-[var(--text-muted)] hover:text-[var(--tiffany)] mb-8" data-testid="reset-back-to-login">
                    <ArrowLeft size={12} /> Back to sign in
                </Link>
                <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-white tracking-tighter mb-3">
                    Choose a new <span className="text-gold-gradient">password</span>
                </h1>

                {!tokenValid ? (
                    <div className="glass-elevated rounded-2xl p-6" data-testid="reset-invalid">
                        <p className="text-sm text-white mb-2 font-semibold">This link isn't valid</p>
                        <p className="text-xs text-[var(--text-secondary)] leading-relaxed mb-4">
                            Password reset links expire after 60 minutes and can only be used once. Please request a fresh link.
                        </p>
                        <Link to="/forgot-password" className="btn-solid inline-flex items-center gap-2" data-testid="reset-request-new">
                            Request new link
                        </Link>
                    </div>
                ) : done ? (
                    <div className="glass-elevated rounded-2xl p-6 flex items-start gap-3" data-testid="reset-done">
                        <CheckCircle2 size={20} className="text-[var(--tiffany)] shrink-0 mt-0.5" />
                        <div>
                            <p className="text-sm font-semibold text-white mb-1">Password updated</p>
                            <p className="text-xs text-[var(--text-secondary)]">Signing you in…</p>
                        </div>
                    </div>
                ) : (
                    <form onSubmit={submit} className="glass-elevated rounded-2xl p-6 space-y-4" data-testid="reset-password-form">
                        <div>
                            <label className="block text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1.5">New password</label>
                            <div className="relative">
                                <KeyRound size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                                <input
                                    type="password"
                                    required
                                    minLength={8}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="At least 8 characters"
                                    className="w-full min-w-0 pl-9 pr-4 py-2.5 bg-black/40 border border-white/10 rounded-full text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--gold)]"
                                    data-testid="reset-password-input"
                                    autoComplete="new-password"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1.5">Confirm password</label>
                            <div className="relative">
                                <KeyRound size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                                <input
                                    type="password"
                                    required
                                    minLength={8}
                                    value={confirm}
                                    onChange={(e) => setConfirm(e.target.value)}
                                    placeholder="Type the password again"
                                    className="w-full min-w-0 pl-9 pr-4 py-2.5 bg-black/40 border border-white/10 rounded-full text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--gold)]"
                                    data-testid="reset-confirm-input"
                                    autoComplete="new-password"
                                />
                            </div>
                        </div>
                        <button type="submit" disabled={submitting} className="btn-solid w-full inline-flex items-center justify-center gap-2" data-testid="reset-submit-btn">
                            {submitting ? "Updating…" : "Update password"}
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
}
